import { Composable, Inject, Setup, Vue } from '@natureos/vue-facing-decorator';

import type { ApiResponse, ApiService } from '@/common/services/api.service-new';
import type { MultiPolygonCoords } from '../hansen/hansen.types';
import { GLC_FCS30D_DEFAULT_YEAR } from './glc-fcs30d.const';
import { useGlcFcs30dStore } from './glc-fcs30d.store';
import type {
  GlcFcs30dLayerResponse,
  GlcFcs30dStatsResponse,
  GlcFcs30dTimeseriesResponse,
} from './glc-fcs30d.types';

/**
 * GLC_FCS30D global land cover (30 m, 26 discrete epochs 1985–2022) — sibling of
 * `useLulcService` / `useEsaCciService`, but single-scheme (native LCCS, static
 * legend). Tiles are served from JWT-protected `/glc-fcs30d/tiles/{z}/{x}/{y}?year=`
 * endpoints (Lambda@Edge verifies the bearer); `ApiService` attaches the token on
 * the layer/stats/timeseries calls, and the tile fetcher routes through the raster
 * service so the bearer rides on the tile requests too.
 *
 * Contract: ~/.claude/contract/260623_glc_fcs30d.md
 */
@Composable()
export class useGlcFcs30dService extends Vue {
  @Inject()
  private readonly api!: ApiService;

  @Setup(() => useGlcFcs30dStore())
  public readonly glcFcs30dStore!: ReturnType<typeof useGlcFcs30dStore>;

  /**
   * Tile-URL template for `year` (`?year=` already baked in by the BE). Cached per
   * year in `glcFcs30dStore`; `signal` only matters on the cache-miss path since
   * the cached return is synchronous.
   */
  public async getGlcFcs30dLayer(
    year: number = GLC_FCS30D_DEFAULT_YEAR,
    signal?: AbortSignal,
  ): Promise<ApiResponse<GlcFcs30dLayerResponse>> {
    const cached = this.glcFcs30dStore.getLayer(year);
    if (cached) {
      return cached;
    }

    const res = await this.api.get<GlcFcs30dLayerResponse>(
      `${import.meta.env.VITE_LULC_API}/glc-fcs30d/layer?year=${year}`,
      { signal },
    );

    if (res.isSuccess && res.data) {
      this.glcFcs30dStore.setLayer(year, res);
    }
    return res;
  }

  /** Per-class hectares in `coords` at `year`. */
  public async getGlcFcs30dStats(
    coords: MultiPolygonCoords,
    year: number = GLC_FCS30D_DEFAULT_YEAR,
    signal?: AbortSignal,
  ): Promise<ApiResponse<GlcFcs30dStatsResponse>> {
    return this.api.post<GlcFcs30dStatsResponse>(
      `${import.meta.env.VITE_LULC_API}/glc-fcs30d/stats?year=${year}`,
      { data: coords },
      { signal },
    );
  }

  /**
   * Per-class hectares for EVERY epoch in `coords` — powers the stacked-area
   * yearly-distribution chart. One call returns the full 1985–2022 series (no year
   * param). Pending contract addition (ask #5); until it ships this 404s / errors
   * and the chart shows a neutral empty state.
   */
  public async getGlcFcs30dTimeseries(
    coords: MultiPolygonCoords,
    signal?: AbortSignal,
  ): Promise<ApiResponse<GlcFcs30dTimeseriesResponse>> {
    return this.api.post<GlcFcs30dTimeseriesResponse>(
      `${import.meta.env.VITE_LULC_API}/glc-fcs30d/timeseries`,
      { data: coords },
      { signal },
    );
  }
}
